/**
 * Tanıtım sitesinde "aktif" diye ilan edilen entegrasyonlar — TEK liste.
 *
 * Hero'daki şerit (`hero-integrations.tsx`) ve Kapsam bölümü (`platforms.tsx`)
 * ikisi de buradan okuyor; biri "5 reklam platformu" derken öteki dört tane
 * saymasın.
 *
 * ── Listeye girme ölçütü ──────────────────────────────────────────────────
 *
 * 🔴 Kullanıcı kararı (14 Eki 2026): bağdaştırıcısı YAZILMIŞ her platform
 * sitede "aktif" görünür — `ConnectorRegistry.isImplemented()` (5 reklam) ve
 * `StoreConnectorRegistry.isImplemented()` (11 mağaza) ne diyorsa o. OpenAI
 * yok: bağdaştırıcısı yok.
 *
 * Aynı gün `ad_connections` / `store_connections` okunduğunda GERÇEK bir
 * hesapla uçtan uca bağlanmış olanlar: Meta, X, TikTok (7 Eki'de jeton
 * iptal edildi), Shopify, Trendyol, ikas, WooCommerce, T-Soft. Kalanlar
 * sitede aktif ama kanıtsız — müşteri bağlamayı denerse takılacağı yerler:
 *
 *   Google Ads    hiç bağlantı yok, hiç denenmedi
 *   LinkedIn      Development tier: yalnız portala elle eklenen 5 hesap görünür
 *   Hepsiburada   entegratör kaydı yok, satıcı bize kimlik açamıyor
 *   Amazon        `.env`de SP-API anahtarları yok + OAuth dönüşü kırık
 *   İdeaSoft      API adresleri ölçümle uyuşmuyordu; kayıt self-servis değil
 *   Ticimax · n11 · Etsy   hiç gerçek mağazaya karşı koşmadı
 *
 * (Ayrıntılar hafızada: amazon-baglantisi-bes-engel, hepsiburada-entegrator-
 * kaydi-sart, ideasoft-api-adresleri-yanlis, linkedin-scope-degisimi-…)
 * Yeni platform = buraya satır + `messages/{en,tr}.json`da adı geçen cümleler
 * (`hero.description`, `platforms.lead`, `faq.items.platforms.a`, …).
 *
 * ── İşaretler ─────────────────────────────────────────────────────────────
 *
 * `mark` beyaz zemin, `markDark` hero'nun koyu zemini için. Kaynaklar:
 * Meta, TikTok, X, LinkedIn → svgl.app (`*-dark.svg` svgl'in `dark`
 * varyantı). Google Ads → wask'ın resmî işareti (svgl'de yok). Mağazalar →
 * `public/logos/stores` (kaynak tablosu `dashboard/stores/store-bits.tsx`
 * başında). `*-dark` mağaza dosyaları aynı dosyanın koyu dolgusu (siyah,
 * lacivert, koyu gri, İdeaSoft moru) beyaza çevrilmiş hâli;
 * `trendyol-mark.png` uygulama simgesinin beyaz kenar payı kırpılıp köşeleri
 * saydamlaştırılmış hâli (iki zeminde de aynı dosya).
 *
 * `w`/`h` yalnız en/boy oranı — `<img>` niteliği olarak yer ayırıyor, boyu
 * CSS veriyor. `scale` ortak yükseklikten sonra kalan optik payın TAHMİNİ
 * (sunucuda rasterleyici kullanılmıyor): geniş Woo balonu ve dolu karolar
 * (Trendyol, Hepsiburada, LinkedIn) aynı boyda ağır basıyor, ince Meta şeridi
 * hafif kalıyor. Görsel kontrolde düzeltilebilir.
 */
export interface SiteIntegration {
  key: string;
  /** Marka adı — çevrilmez. */
  name: string;
  mark: string;
  markDark: string;
  w: number;
  h: number;
  scale: number;
  /**
   * Kullanıcının bağlarken gördüğü yol. Reklam kanallarının hepsi OAuth.
   * Mağazalarda shared `commerce.ts` → `STORE_PLATFORM_AUTH_MODE`ın AYNASI —
   * oradan import edilmiyor, çünkü `@mentorivo/shared` kökü tanıtım sayfasının
   * istemci paketine bütün sözleşme katmanını (zod şemaları) taşırdı.
   */
  auth: 'oauth' | 'apiKey';
}

export interface SiteStoreIntegration extends SiteIntegration {
  /** E-ticaret altyapısı (mağazanın kendisi) mı, pazaryeri mi. */
  kind: 'platform' | 'marketplace';
}

export const AD_INTEGRATIONS = [
  {
    key: 'meta',
    name: 'Meta',
    mark: '/logos/brands/meta.svg',
    markDark: '/logos/brands/meta.svg',
    w: 256,
    h: 171,
    scale: 0.93,
    auth: 'oauth',
  },
  {
    key: 'google',
    name: 'Google Ads',
    mark: '/logos/google-ads.svg',
    markDark: '/logos/google-ads.svg',
    w: 26,
    h: 26,
    scale: 1,
    auth: 'oauth',
  },
  {
    key: 'tiktok',
    name: 'TikTok',
    mark: '/logos/brands/tiktok.svg',
    markDark: '/logos/brands/tiktok-dark.svg',
    w: 352,
    h: 399,
    scale: 1.12,
    auth: 'oauth',
  },
  {
    key: 'linkedin',
    name: 'LinkedIn',
    mark: '/logos/brands/linkedin.svg',
    markDark: '/logos/brands/linkedin.svg',
    w: 256,
    h: 256,
    scale: 0.88,
    auth: 'oauth',
  },
  {
    key: 'x',
    name: 'X',
    mark: '/logos/brands/x.svg',
    markDark: '/logos/brands/x-dark.svg',
    w: 1200,
    h: 1227,
    scale: 0.86,
    auth: 'oauth',
  },
] as const satisfies readonly SiteIntegration[];

/** Sıra: önce e-ticaret altyapıları, sonra pazaryerleri. */
export const STORE_INTEGRATIONS = [
  {
    key: 'shopify',
    name: 'Shopify',
    mark: '/logos/stores/shopify.svg',
    markDark: '/logos/stores/shopify.svg',
    w: 246,
    h: 286,
    scale: 1.04,
    auth: 'oauth',
    kind: 'platform',
  },
  {
    key: 'woocommerce',
    name: 'WooCommerce',
    mark: '/logos/stores/woocommerce.svg',
    markDark: '/logos/stores/woocommerce.svg',
    w: 256,
    h: 153,
    scale: 0.86,
    auth: 'apiKey',
    kind: 'platform',
  },
  {
    key: 'ikas',
    name: 'ikas',
    mark: '/logos/stores/ikas.svg',
    markDark: '/logos/stores/ikas-dark.svg',
    w: 232,
    h: 225,
    scale: 0.94,
    auth: 'apiKey',
    kind: 'platform',
  },
  {
    key: 'tsoft',
    name: 'T-Soft',
    mark: '/logos/stores/tsoft.svg',
    markDark: '/logos/stores/tsoft-dark.svg',
    w: 303,
    h: 360,
    scale: 1,
    auth: 'apiKey',
    kind: 'platform',
  },
  {
    key: 'ticimax',
    name: 'Ticimax',
    mark: '/logos/stores/ticimax.svg',
    markDark: '/logos/stores/ticimax-dark.svg',
    w: 48,
    h: 48,
    scale: 0.95,
    auth: 'apiKey',
    kind: 'platform',
  },
  {
    key: 'ideasoft',
    name: 'İdeaSoft',
    mark: '/logos/stores/ideasoft.svg',
    markDark: '/logos/stores/ideasoft-dark.svg',
    w: 415,
    h: 440,
    scale: 1,
    auth: 'apiKey',
    kind: 'platform',
  },
  {
    key: 'trendyol',
    name: 'Trendyol',
    mark: '/logos/stores/trendyol-mark.png',
    markDark: '/logos/stores/trendyol-mark.png',
    w: 144,
    h: 144,
    scale: 0.9,
    auth: 'apiKey',
    kind: 'marketplace',
  },
  {
    key: 'hepsiburada',
    name: 'Hepsiburada',
    mark: '/logos/stores/hepsiburada.png',
    markDark: '/logos/stores/hepsiburada.png',
    w: 48,
    h: 48,
    scale: 0.9,
    auth: 'apiKey',
    kind: 'marketplace',
  },
  {
    key: 'n11',
    name: 'n11',
    mark: '/logos/stores/n11.svg',
    markDark: '/logos/stores/n11.svg',
    w: 196,
    h: 226,
    scale: 1,
    auth: 'apiKey',
    kind: 'marketplace',
  },
  {
    key: 'amazon',
    name: 'Amazon',
    mark: '/logos/stores/amazon.svg',
    markDark: '/logos/stores/amazon-dark.svg',
    w: 251,
    h: 260,
    scale: 1,
    auth: 'oauth',
    kind: 'marketplace',
  },
  {
    key: 'etsy',
    name: 'Etsy',
    mark: '/logos/stores/etsy.svg',
    markDark: '/logos/stores/etsy.svg',
    w: 24,
    h: 24,
    scale: 0.95,
    auth: 'oauth',
    kind: 'marketplace',
  },
] as const satisfies readonly SiteStoreIntegration[];
